import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Timer, Play, Pause, Clock } from "lucide-react";

function formatTime(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function ReadingTimer({ api, paperId, pagesRead }) {
  const [sessionId, setSessionId] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [running, setRunning] = useState(false);
  const intervalRef = useRef(null);
  const heartbeatRef = useRef(null);

  // Start session on mount
  useEffect(() => {
    const startSession = async () => {
      try {
        const res = await axios.post(`${api}/reading-sessions/start`, { paper_id: paperId }, { withCredentials: true });
        setSessionId(res.data.id);
        setRunning(true);
        setElapsed(0);
      } catch {
        // Non-blocking
      }
    };
    startSession();

    // End session on unmount
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    };
  }, [api, paperId]);

  // End session when component unmounts (navigating away)
  useEffect(() => {
    const currentSessionId = sessionId;
    const currentPagesRead = pagesRead;
    return () => {
      if (currentSessionId) {
        const url = `${api}/reading-sessions/end`;
        fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: currentSessionId, pages_read: currentPagesRead }),
          credentials: "include",
          keepalive: true,
        }).catch(() => {});
      }
    };
  }, [sessionId, pagesRead, api]);

  // Timer tick
  useEffect(() => {
    if (running) {
      intervalRef.current = setInterval(() => {
        setElapsed((prev) => prev + 1);
      }, 1000);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [running]);

  // Heartbeat every 30 seconds
  useEffect(() => {
    if (running && sessionId) {
      heartbeatRef.current = setInterval(async () => {
        try {
          await axios.post(`${api}/reading-sessions/heartbeat`, { session_id: sessionId }, { withCredentials: true });
        } catch {
          // Non-blocking
        }
      }, 30000);
    }
    return () => {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    };
  }, [running, sessionId, api]);

  const togglePause = () => setRunning((prev) => !prev);

  const endSession = async () => {
    if (!sessionId) return;
    try {
      await axios.post(`${api}/reading-sessions/end`, {
        session_id: sessionId,
        pages_read: pagesRead,
      }, { withCredentials: true });
      setRunning(false);
      setSessionId(null);
    } catch {
      // ignore
    }
  };

  return (
    <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444]" data-testid="reading-timer">
      <div className="flex items-center gap-2 mb-3">
        <Timer className="w-5 h-5 text-neo-green" />
        <h3 className="font-heading font-bold dark:text-white">Reading Timer</h3>
      </div>
      <div className="text-center">
        <p className="font-heading font-extrabold text-3xl tracking-tight dark:text-white" data-testid="timer-display">
          {formatTime(elapsed)}
        </p>
        <p className="text-xs text-neo-text-secondary dark:text-gray-400 mt-1">
          {running ? "Reading..." : "Paused"}
        </p>
      </div>
      <div className="flex gap-2 mt-3">
        <button
          onClick={togglePause}
          className={`neo-btn flex-1 flex items-center justify-center gap-1 text-sm ${running ? "bg-neo-yellow" : "neo-btn-success"}`}
          data-testid="timer-toggle-btn"
        >
          {running ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
          {running ? "Pause" : "Resume"}
        </button>
      </div>
    </div>
  );
}

// Compact timer for the top bar
export function TimerBadge({ elapsed, running }) {
  return (
    <div className={`neo-badge text-xs ${running ? "bg-neo-green" : "bg-gray-200 dark:bg-gray-600"}`} data-testid="timer-badge">
      <Clock className="w-3 h-3" />
      <span>{formatTime(elapsed)}</span>
    </div>
  );
}
